import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { RefreshCcw, Mail, Send, AlertTriangle, TrendingDown, DollarSign, Settings2, Package, ShoppingCart } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, tone = "primary", testid, hint }) => (
  <div className="border bg-card p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} className={tone === "critical" ? "text-red-600" : tone === "primary" ? "text-primary" : "text-muted-foreground"} />
      <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
    </div>
    <div className={`text-3xl font-black tabular-nums leading-none ${tone === "critical" ? "text-red-600" : ""}`}>{value}</div>
    {hint && <div className="text-[10px] text-muted-foreground mt-2">{hint}</div>}
  </div>
);

export default function Reabasto() {
  const { t } = useI18n();
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({}); // {item_number: bool}

  // Recalc dialog
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [recalcForm, setRecalcForm] = useState({ lead_time_days: 14, coverage_days: 30, safety_factor: 1.2, only_with_consumption: true });
  const [preview, setPreview] = useState(null);

  // Settings dialog
  const [setOpen, setSetOpen] = useState(false);
  const [alertSettings, setAlertSettings] = useState({ enabled: false, recipients: [], hour_utc: 14, lead_time_days: 14, coverage_days: 30 });
  const [recipientsRaw, setRecipientsRaw] = useState("");

  // Simulator dialog
  const [simOpen, setSimOpen] = useState(false);
  const [simData, setSimData] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  // Bulk supplier assignment
  const [bulkOpen, setBulkOpen] = useState(false);
  const [supList, setSupList] = useState([]);
  const [assignMap, setAssignMap] = useState({}); // { item_number: supplier_id }
  const [applyAllSup, setApplyAllSup] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reabasto");
      setData(r.data);
      setAlertSettings(r.data.settings);
      setRecipientsRaw((r.data.settings.recipients || []).join(", "));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const editable = can("admin", "buyer");

  const doPreview = async () => {
    try {
      const r = await api.post("/items/recalc-stock-policy", { ...recalcForm, apply: false });
      setPreview(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doApply = async () => {
    try {
      const r = await api.post("/items/recalc-stock-policy", { ...recalcForm, apply: true });
      toast.success(t("rb.recalcDone").replace("{n}", r.data.applied));
      setRecalcOpen(false); setPreview(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendAlert = async () => {
    try {
      const r = await api.post("/reabasto/send-alert-now");
      if (r.data.error) toast.error(r.data.error);
      else toast.success(t("rb.alertSent").replace("{n}", (r.data.sent_to || []).length));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveSettings = async () => {
    try {
      const recipients = recipientsRaw.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = { ...alertSettings, recipients };
      const r = await api.put("/reabasto/settings", payload);
      setAlertSettings(r.data);
      toast.success(t("rb.settingsSaved"));
      setSetOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createRequisition = async () => {
    const nums = Object.keys(selected).filter((k) => selected[k]);
    if (nums.length === 0) return toast.error("Selecciona al menos un artículo");
    try {
      const r = await api.post("/stock-alerts/requisition", { item_numbers: nums });
      toast.success(`Requisición ${r.data.number} creada`);
      setSelected({});
      navigate("/requisitions");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleAll = () => {
    if (!data) return;
    if (Object.values(selected).some(Boolean)) setSelected({});
    else setSelected(Object.fromEntries(data.items.map((a) => [a.item_number, true])));
  };

  const runSimulator = async (apply = false) => {
    const nums = Object.keys(selected).filter((k) => selected[k]);
    setSimLoading(true);
    try {
      const r = await api.post("/reabasto/simulate-purchases", {
        item_numbers: nums.length > 0 ? nums : null, apply,
      });
      setSimData(r.data);
      if (apply) {
        toast.success(t("rb.simulateDone").replace("{n}", (r.data.created_pos || []).length));
      }
      if (!simOpen) setSimOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSimLoading(false); }
  };

  const openBulkAssign = async () => {
    if (!simData || simData.unassigned.line_count === 0) return;
    if (supList.length === 0) {
      const r = await api.get("/address-book?type=supplier");
      setSupList(r.data);
    }
    setAssignMap({});
    setApplyAllSup("");
    setBulkOpen(true);
  };

  const applyToAllRows = (supId) => {
    if (!supId || !simData) return;
    const m = {};
    simData.unassigned.lines.forEach((l) => { m[l.item_number] = supId; });
    setAssignMap(m);
  };

  const saveAssignments = async () => {
    const assignments = Object.entries(assignMap)
      .filter(([, sid]) => sid && sid !== "none")
      .map(([item_number, supplier_id]) => ({ item_number, supplier_id }));
    if (assignments.length === 0) return toast.error("No hay asignaciones");
    try {
      const r = await api.post("/items/bulk-set-supplier", { assignments });
      toast.success(t("sim.assignmentsSaved").replace("{n}", r.data.updated));
      setBulkOpen(false);
      // Re-run simulator to reflect the changes
      runSimulator(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="text-xs text-muted-foreground p-6">Cargando…</div>;

  const t_ = data.totals;

  return (
    <div data-testid="reabasto-page">
      <PageHeader title={t("rb.title")} subtitle={t("rb.subtitle")}>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={load} className="rounded-none text-xs font-bold" data-testid="rb-refresh">
            <RefreshCcw size={13} className="mr-1" /> {t("common.refresh") || "Actualizar"}
          </Button>
          {editable && (
            <>
              <Button size="sm" variant="outline" onClick={() => setRecalcOpen(true)} className="rounded-none text-xs font-bold" data-testid="rb-recalc-button">
                <TrendingDown size={13} className="mr-1" /> {t("rb.recalc")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => runSimulator(false)} disabled={simLoading}
                className="rounded-none text-xs font-bold" data-testid="rb-simulate-button">
                <ShoppingCart size={13} className="mr-1" /> {t("rb.simulator")}
              </Button>
              <Button size="sm" variant="outline" onClick={sendAlert} className="rounded-none text-xs font-bold" data-testid="rb-send-now-button">
                <Send size={13} className="mr-1" /> {t("rb.sendAlert")}
                {alertSettings.recipients.length === 0 && (
                  <span className="ml-1 text-[9px] text-amber-600" title={t("rb.noRecipientsWarn")}>⚠</span>
                )}
              </Button>
              {can("admin") && (
                <Button size="sm" onClick={() => setSetOpen(true)} className="rounded-none text-xs font-bold" data-testid="rb-settings-button">
                  <Settings2 size={13} className="mr-1" /> {t("rb.alertSettings")}
                </Button>
              )}
            </>
          )}
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard icon={Package} label={t("rb.itemsCount")} value={t_.items_count} testid="rb-kpi-items" />
        <StatCard icon={AlertTriangle} label={t("rb.criticalCount")} value={t_.critical_count}
          tone={t_.critical_count > 0 ? "critical" : "primary"} testid="rb-kpi-critical" />
        <StatCard icon={DollarSign} label={t("rb.cashImpact")} value={fmtMoney(t_.cash_impact)} testid="rb-kpi-cash" />
        <StatCard icon={RefreshCcw} label={`${t("rb.windowDays")} / ${t("rb.leadTime")}`}
          value={`${t_.window_days} / ${t_.lead_time_days}`} hint={t("rb.days")} testid="rb-kpi-window" />
      </div>

      {alertSettings.last_sent && (
        <div className="text-[10px] text-muted-foreground mb-3 flex items-center gap-1">
          <Mail size={11} /> {t("rb.lastSent")}: {fmtDate(alertSettings.last_sent)}
          {alertSettings.enabled && alertSettings.recipients.length > 0 && (
            <span className="ml-2">· {alertSettings.recipients.length} {t("rb.recipients").split("(")[0].toLowerCase().trim()}</span>
          )}
        </div>
      )}

      {data.items.length === 0 ? (
        <Empty text={t("rb.noItems")} />
      ) : (
        <>
          {editable && (
            <div className="flex justify-between items-center mb-2">
              <button onClick={toggleAll} className="text-[11px] font-bold text-primary hover:underline" data-testid="rb-select-all">
                {t("rb.selectAll")}
              </button>
              <Button size="sm" onClick={createRequisition} disabled={!Object.values(selected).some(Boolean)}
                className="rounded-none text-xs font-bold" data-testid="rb-create-req">
                {t("rb.createReq")}
              </Button>
            </div>
          )}
          <div className="tbl-wrap" data-testid="rb-table">
            <table className="tbl">
              <thead><tr>
                {editable && <th style={{ width: 24 }}></th>}
                <th>{t("rb.item")}</th>
                <th className="num">{t("rb.onHand")}</th>
                <th className="num">{t("rb.reorder")}</th>
                <th className="num">{t("rb.max")}</th>
                <th className="num">{t("rb.shortage")}</th>
                <th className="num">{t("rb.avgDaily")}</th>
                <th className="num">{t("rb.projected30")}</th>
                <th className="num">{t("rb.daysCover")}</th>
                <th className="num">{t("rb.estTotal")}</th>
              </tr></thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.item_number} data-testid={`rb-row-${a.item_number}`}
                    className={a.critical ? "bg-red-50 dark:bg-red-950/30" : ""}>
                    {editable && (
                      <td>
                        <input type="checkbox" checked={!!selected[a.item_number]}
                          onChange={(e) => setSelected({ ...selected, [a.item_number]: e.target.checked })}
                          data-testid={`rb-check-${a.item_number}`} />
                      </td>
                    )}
                    <td>
                      <div className="font-bold tabular-nums">{a.item_number}</div>
                      <div className="text-[10px] text-muted-foreground truncate max-w-md">{a.description}</div>
                      {a.critical && <span className="inline-block mt-1 px-1.5 py-0 text-[9px] font-black bg-red-600 text-white">{t("rb.critical")}</span>}
                    </td>
                    <td className="num tabular-nums">{fmtQty(a.on_hand)} <span className="text-[9px] text-muted-foreground">{a.uom}</span></td>
                    <td className="num tabular-nums">{fmtQty(a.reorder_point)}</td>
                    <td className="num tabular-nums">{fmtQty(a.max_stock)}</td>
                    <td className="num tabular-nums font-bold text-primary">{fmtQty(a.shortage)}</td>
                    <td className="num tabular-nums">{a.avg_daily_consumption > 0 ? fmtQty(a.avg_daily_consumption) : "—"}</td>
                    <td className="num tabular-nums">{a.projected_30d > 0 ? fmtQty(a.projected_30d) : "—"}</td>
                    <td className={`num tabular-nums font-bold ${a.critical ? "text-red-600" : ""}`}>
                      {a.days_of_cover != null ? `${a.days_of_cover} d` : "—"}
                    </td>
                    <td className="num tabular-nums">{fmtMoney(a.est_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Recalc dialog */}
      <Dialog open={recalcOpen} onOpenChange={(o) => { setRecalcOpen(o); if (!o) setPreview(null); }}>
        <DialogContent className="rounded-none sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">{t("rb.recalcTitle")}</DialogTitle>
            <DialogDescription className="text-xs">{t("rb.recalcHint")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase">{t("rb.leadDays")}</Label>
              <Input type="number" min="1" value={recalcForm.lead_time_days}
                onChange={(e) => setRecalcForm({ ...recalcForm, lead_time_days: parseInt(e.target.value) || 14 })}
                className="rounded-none h-8 text-xs" data-testid="rb-recalc-lead" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase">{t("rb.coverageDays")}</Label>
              <Input type="number" min="1" value={recalcForm.coverage_days}
                onChange={(e) => setRecalcForm({ ...recalcForm, coverage_days: parseInt(e.target.value) || 30 })}
                className="rounded-none h-8 text-xs" data-testid="rb-recalc-coverage" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase">{t("rb.safetyFactor")}</Label>
              <Input type="number" min="1" step="0.1" value={recalcForm.safety_factor}
                onChange={(e) => setRecalcForm({ ...recalcForm, safety_factor: parseFloat(e.target.value) || 1.2 })}
                className="rounded-none h-8 text-xs" data-testid="rb-recalc-safety" />
            </div>
            <div className="col-span-3 flex items-center gap-2">
              <Switch checked={recalcForm.only_with_consumption}
                onCheckedChange={(v) => setRecalcForm({ ...recalcForm, only_with_consumption: v })}
                data-testid="rb-recalc-only-consumption" />
              <Label className="text-[10px] font-bold uppercase">{t("rb.onlyWithConsumption")}</Label>
            </div>
          </div>
          {preview && (
            <div className="border mt-3 max-h-96 overflow-auto">
              <table className="tbl">
                <thead><tr>
                  <th>{t("rb.item")}</th>
                  <th className="num">{t("rb.avgDaily")}</th>
                  <th>{t("rb.current")}</th>
                  <th>{t("rb.proposal")}</th>
                </tr></thead>
                <tbody>
                  {preview.proposals.map((p) => (
                    <tr key={p.item_number} data-testid={`rb-preview-${p.item_number}`}
                      className={p.changed ? "" : "opacity-50"}>
                      <td>
                        <div className="font-bold tabular-nums">{p.item_number}</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-xs">{p.description}</div>
                      </td>
                      <td className="num tabular-nums">{p.avg_daily_consumption}</td>
                      <td className="text-[10px] tabular-nums">
                        R:{p.current.reorder_point} · Máx:{p.current.max_stock} · Mín:{p.current.min_stock}
                      </td>
                      <td className="text-[10px] tabular-nums font-bold">
                        {p.changed ? (
                          <span className="text-primary">R:{p.proposed.reorder_point} · Máx:{p.proposed.max_stock} · Mín:{p.proposed.min_stock}</span>
                        ) : <span className="text-muted-foreground">{t("rb.noChange")}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 text-[11px] text-muted-foreground">
                Total: {preview.count} · Cambios: {preview.proposals.filter((p) => p.changed).length}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={doPreview} className="rounded-none text-xs font-bold" data-testid="rb-preview-button">
              {t("rb.recalcPreview")}
            </Button>
            <Button onClick={doApply} disabled={!preview}
              className="rounded-none text-xs font-bold" data-testid="rb-apply-button">
              {t("rb.recalcApply")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Simulator dialog */}
      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent className="rounded-none sm:max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">{t("rb.simulateTitle")}</DialogTitle>
            <DialogDescription className="text-xs">{t("rb.simulateHint")}</DialogDescription>
          </DialogHeader>
          {simData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div className="border p-2"><div className="text-[9px] uppercase text-muted-foreground tracking-widest">{t("rb.supplier")}s</div><div className="text-lg font-black tabular-nums">{simData.totals.supplier_count}</div></div>
                <div className="border p-2"><div className="text-[9px] uppercase text-muted-foreground tracking-widest">Líneas</div><div className="text-lg font-black tabular-nums">{simData.totals.line_count}</div></div>
                <div className="border p-2"><div className="text-[9px] uppercase text-muted-foreground tracking-widest">{t("rb.grandTotal")}</div><div className="text-lg font-black tabular-nums">{fmtMoney(simData.totals.grand_total)}</div></div>
                <div className="border p-2"><div className="text-[9px] uppercase text-muted-foreground tracking-widest">{t("rb.unassigned")}</div><div className={`text-lg font-black tabular-nums ${simData.totals.unassigned_lines > 0 ? "text-amber-600" : ""}`}>{simData.totals.unassigned_lines}</div></div>
              </div>
              {simData.suggestions.map((s) => (
                <div key={s.supplier_id} className="border" data-testid={`sim-sup-${s.supplier_id}`}>
                  <div className="bg-muted/40 p-2 flex items-center justify-between">
                    <div><span className="font-black text-xs">{s.supplier_name}</span> <span className="text-[10px] text-muted-foreground">· {s.line_count} líneas · {s.supplier_currency}</span></div>
                    <div className="text-sm font-black tabular-nums text-primary">{fmtMoney(s.subtotal)}</div>
                  </div>
                  <table className="tbl">
                    <thead><tr><th>Artículo</th><th className="num">Qty</th><th className="num">Costo unit.</th><th className="num">Subtotal</th></tr></thead>
                    <tbody>
                      {s.lines.map((l) => (
                        <tr key={l.item_number}>
                          <td><span className="font-bold tabular-nums">{l.item_number}</span> · <span className="text-[11px]">{l.description}</span></td>
                          <td className="num tabular-nums">{fmtQty(l.qty)} <span className="text-[9px] text-muted-foreground">{l.uom}</span></td>
                          <td className="num tabular-nums">{fmtMoney(l.unit_cost)}</td>
                          <td className="num tabular-nums font-bold">{fmtMoney(l.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {simData.unassigned.line_count > 0 && (
                <div className="border border-amber-400">
                  <div className="bg-amber-50 dark:bg-amber-950/30 p-2 flex items-center justify-between">
                    <span className="font-bold text-xs text-amber-700 dark:text-amber-400">⚠ {t("rb.unassigned")} · {simData.unassigned.line_count} líneas</span>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={openBulkAssign} className="rounded-none text-[10px] font-bold h-7" data-testid="sim-bulk-assign">
                        {t("sim.bulkAssign")}
                      </Button>
                      <span className="text-sm font-black tabular-nums">{fmtMoney(simData.unassigned.subtotal)}</span>
                    </div>
                  </div>
                  <table className="tbl">
                    <tbody>
                      {simData.unassigned.lines.map((l) => (
                        <tr key={l.item_number}>
                          <td><span className="font-bold tabular-nums">{l.item_number}</span> · <span className="text-[11px]">{l.description}</span></td>
                          <td className="num tabular-nums">{fmtQty(l.qty)}</td>
                          <td className="num tabular-nums">{fmtMoney(l.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="p-2 text-[10px] text-muted-foreground">Estos artículos no tienen proveedor preferente; configúralo en Maestro de Artículos → Editar.</div>
                </div>
              )}
              {simData.created_pos && simData.created_pos.length > 0 && (
                <div className="border border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 p-3">
                  <div className="text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-2">✓ OCs generadas:</div>
                  {simData.created_pos.map((p) => (
                    <div key={p.po_number} className="text-xs">
                      <a href="/purchase-orders" className="font-bold underline">{p.po_number}</a> · {p.supplier_name} · {p.line_count} líneas · {fmtMoney(p.total)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => runSimulator(false)} disabled={simLoading} className="rounded-none text-xs font-bold" data-testid="sim-refresh">
              {t("rb.simulateRun")}
            </Button>
            {simData && simData.suggestions.length > 0 && !simData.created_pos && (
              <Button onClick={() => runSimulator(true)} disabled={simLoading}
                className="rounded-none text-xs font-bold" data-testid="sim-apply">
                {t("rb.simulateApply")} ({simData.totals.supplier_count})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Alert settings dialog */}
      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">{t("rb.alertSettings")}</DialogTitle>
            <DialogDescription className="text-xs">{t("rb.hourUtcHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={alertSettings.enabled}
                onCheckedChange={(v) => setAlertSettings({ ...alertSettings, enabled: v })}
                data-testid="rb-set-enabled" />
              <Label className="text-[10px] font-bold uppercase">{t("rb.alertsEnabled")}</Label>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase">{t("rb.recipients")}</Label>
              <Input value={recipientsRaw} onChange={(e) => setRecipientsRaw(e.target.value)}
                placeholder="comprador1@empresa.com, comprador2@empresa.com"
                className="rounded-none h-8 text-xs" data-testid="rb-set-recipients" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase">{t("rb.hourUtc")}</Label>
                <Input type="number" min="0" max="23" value={alertSettings.hour_utc}
                  onChange={(e) => setAlertSettings({ ...alertSettings, hour_utc: parseInt(e.target.value) || 0 })}
                  className="rounded-none h-8 text-xs" data-testid="rb-set-hour" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase">{t("rb.leadDays")}</Label>
                <Input type="number" min="1" value={alertSettings.lead_time_days}
                  onChange={(e) => setAlertSettings({ ...alertSettings, lead_time_days: parseInt(e.target.value) || 14 })}
                  className="rounded-none h-8 text-xs" data-testid="rb-set-lead" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase">{t("rb.coverageDays")}</Label>
                <Input type="number" min="1" value={alertSettings.coverage_days}
                  onChange={(e) => setAlertSettings({ ...alertSettings, coverage_days: parseInt(e.target.value) || 30 })}
                  className="rounded-none h-8 text-xs" data-testid="rb-set-coverage" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveSettings} className="rounded-none text-xs font-bold" data-testid="rb-settings-save">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk supplier assignment dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="rounded-none sm:max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="uppercase font-black">{t("sim.bulkAssignTitle")}</DialogTitle>
            <DialogDescription className="text-xs">{t("sim.bulkHint")}</DialogDescription>
          </DialogHeader>
          {/* Apply-all row */}
          <div className="flex items-center gap-2 border p-2 bg-muted/30">
            <Label className="text-[10px] font-bold uppercase whitespace-nowrap">{t("sim.applyAllSame")}:</Label>
            <select value={applyAllSup} onChange={(e) => setApplyAllSup(e.target.value)}
              className="rounded-none h-8 text-xs border px-2 flex-1" data-testid="bulk-apply-all-select">
              <option value="">—</option>
              {supList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <Button size="sm" onClick={() => applyToAllRows(applyAllSup)} disabled={!applyAllSup}
              className="rounded-none text-[10px] font-bold h-8" data-testid="bulk-apply-all-btn">
              →
            </Button>
          </div>
          {/* Assignments table */}
          {simData && simData.unassigned.lines.length > 0 && (
            <div className="border max-h-96 overflow-auto mt-2">
              <table className="tbl">
                <thead><tr>
                  <th>{t("rb.item")}</th>
                  <th className="num">{t("rb.shortage")}</th>
                  <th>{t("rb.supplier")}</th>
                </tr></thead>
                <tbody>
                  {simData.unassigned.lines.map((l) => (
                    <tr key={l.item_number} data-testid={`bulk-row-${l.item_number}`}>
                      <td>
                        <div className="font-bold tabular-nums">{l.item_number}</div>
                        <div className="text-[10px] text-muted-foreground truncate max-w-md">{l.description}</div>
                      </td>
                      <td className="num tabular-nums">{fmtQty(l.qty)} <span className="text-[9px] text-muted-foreground">{l.uom}</span></td>
                      <td>
                        <select value={assignMap[l.item_number] || ""}
                          onChange={(e) => setAssignMap({ ...assignMap, [l.item_number]: e.target.value })}
                          className="rounded-none h-7 text-xs border px-1 w-full" data-testid={`bulk-select-${l.item_number}`}>
                          <option value="">—</option>
                          {supList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveAssignments}
              disabled={Object.values(assignMap).filter(Boolean).length === 0}
              className="rounded-none text-xs font-bold" data-testid="bulk-save-button">
              {t("sim.saveAssignments")} ({Object.values(assignMap).filter(Boolean).length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
